import React, { useEffect, useState } from "react";
import { Shield, Users, Gamepad2, Rocket, AlertTriangle, Flag, Zap, Play, Star, Sparkles, DollarSign, TrendingUp, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatCard, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MonetizationApprovals from "@/components/MonetizationApprovals";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [mod, setMod] = useState(null);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [usage, setUsage] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [hubProviders, setHubProviders] = useState([]);
  const [hubUsage, setHubUsage] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/moderation").then((r) => setMod(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/games").then((r) => setGames(r.data));
    api.get("/admin/ai-usage").then((r) => setUsage(r.data));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
    api.get("/asset-hub/providers").then((r) => setHubProviders(r.data)).catch(() => {});
    api.get("/asset-hub/usage").then((r) => setHubUsage(r.data)).catch(() => {});
  };

  const patchProvider = async (id, body) => {
    try { const { data } = await api.patch(`/asset-hub/providers/${id}`, body); setHubProviders((ps) => ps.map((p) => p.id === id ? { ...p, ...data } : p)); }
    catch (e) { /* noop */ }
  };
  useEffect(() => { loadAll(); }, []);
  if (!stats) return <Spinner />;

  const resolve = async (id, action) => { await api.post(`/admin/moderation/${id}/resolve`, { action }); toast.success("Resolved"); loadAll(); };
  const resolveReport = async (id) => { await api.post(`/admin/reports/${id}/resolve`); toast.success("Report closed"); loadAll(); };
  const feature = async (id, field, val) => { await api.post(`/admin/games/${id}/feature`, { [field]: val }); toast.success("Updated"); loadAll(); };
  const takedown = async (id) => { await api.post(`/admin/games/${id}/takedown`); toast.success("Taken down"); loadAll(); };

  const modCount = (mod?.queue?.length || 0) + (mod?.reports?.length || 0) + (mod?.pending_games?.length || 0);

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto">
      <PageHeader title="Admin Dashboard" icon={Shield} subtitle="Platform analytics, moderation and management." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total users" value={stats.total_users} icon={Users} delay={1} />
        <StatCard label="Games created" value={stats.games_created} icon={Gamepad2} accent="#10b981" delay={2} />
        <StatCard label="Published" value={stats.games_published} icon={Rocket} accent="#f59e0b" delay={3} />
        <StatCard label="Total plays" value={stats.total_plays} icon={Play} accent="#a855f7" delay={4} />
        <StatCard label="Pending moderation" value={stats.pending_moderation} icon={AlertTriangle} accent="#ef4444" delay={1} />
        <StatCard label="Open reports" value={stats.open_reports} icon={Flag} accent="#ef4444" delay={2} />
        <StatCard label="Pending approvals" value={stats.pending_approvals} icon={Shield} delay={3} />
        <StatCard label="AI calls" value={stats.ai_calls} icon={Zap} accent="#3b9eff" delay={4} />
      </div>

      <MonetizationApprovals endpoint="/monetization/pending" title="Premium pack approvals" />

      <Tabs defaultValue="moderation">
        <TabsList>
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation">Moderation {modCount > 0 && <span className="ml-1.5 px-1.5 rounded-full bg-red-500 text-white text-[10px]">{modCount}</span>}</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="asset-hub" data-testid="admin-tab-asset-hub">Asset Hub</TabsTrigger>
          <TabsTrigger value="games" data-testid="admin-tab-games">Games</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="ai" data-testid="admin-tab-ai">AI Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="moderation" className="mt-4 space-y-4">
          {modCount === 0 && <div className="rounded-2xl border border-dashed p-10 text-center text-[hsl(var(--muted-foreground))]">🎉 The queue is clear. Nothing to review.</div>}
          {mod?.pending_games?.map((g) => (
            <ModRow key={g.id} icon={g.cover_emoji} title={g.title} sub={`Game by ${g.owner_name} · flagged content`}
              actions={<><Button size="sm" className="rounded-full bg-emerald-500" onClick={() => resolve(mod.queue.find((q) => q.target_id === g.id)?.id || g.id, "approve")} data-testid={`mod-approve-${g.id}`}>Approve</Button><Button size="sm" variant="outline" onClick={() => takedown(g.id)}>Remove</Button></>} />
          ))}
          {mod?.queue?.map((q) => (
            <ModRow key={q.id} icon={q.type === "comment" ? "💬" : "🎮"} title={q.title} sub={`${q.type} · ${q.reason} · by ${q.owner_name}`}
              actions={<><Button size="sm" className="rounded-full bg-emerald-500" onClick={() => resolve(q.id, "approve")} data-testid={`mod-q-approve-${q.id}`}>Approve</Button><Button size="sm" variant="outline" onClick={() => resolve(q.id, "remove")} data-testid={`mod-q-remove-${q.id}`}>Remove</Button></>} />
          ))}
          {mod?.reports?.map((r) => (
            <ModRow key={r.id} icon="🚩" title={`Report: ${r.target_type}`} sub={`${r.reason} · by ${r.reporter_name}`}
              actions={<Button size="sm" variant="outline" onClick={() => resolveReport(r.id)} data-testid={`report-resolve-${r.id}`}>Close report</Button>} />
          ))}
        </TabsContent>

        <TabsContent value="analytics" className="mt-4 space-y-4" data-testid="admin-analytics">
          {!analytics ? <Spinner /> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard label="Total revenue" value={`$${analytics.revenue.total.toFixed(2)}`} icon={DollarSign} accent="#10b981" delay={1} />
                <StatCard label="Active subscribers" value={analytics.subscriptions.active} icon={Star} accent="#f59e0b" delay={2} />
                <StatCard label="New users (7d)" value={analytics.signups.last_7_days} icon={UserPlus} accent="#3b9eff" delay={3} />
                <StatCard label="New users (30d)" value={analytics.signups.last_30_days} icon={TrendingUp} accent="#a855f7" delay={4} />
              </div>
              {analytics.profit && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-profit">
                  <StatCard label="API cost (backend)" value={`$${analytics.profit.api_cost.toFixed(2)}`} icon={DollarSign} accent="#ef4444" delay={1} />
                  <StatCard label="Gross profit" value={`$${analytics.profit.gross_profit.toFixed(2)}`} icon={TrendingUp} accent="#10b981" delay={2} />
                  <StatCard label="Profit margin" value={`${analytics.profit.margin_pct}%`} icon={Star} accent="#a855f7" delay={3} />
                  <StatCard label="ARPU" value={`$${analytics.profit.arpu.toFixed(2)}`} icon={Users} accent="#3b9eff" delay={4} />
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
                  <div className="font-display font-bold mb-3 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-500" /> Revenue by source</div>
                  {Object.entries(analytics.revenue.by_kind).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1.5 capitalize" data-testid={`revenue-${k}`}><span>{k}</span><span className="font-bold">${v.toFixed(2)}</span></div>
                  ))}
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">{analytics.revenue.paid_transactions} paid transactions · Marketplace gross ${analytics.marketplace.gross.toFixed(2)} ({analytics.marketplace.sales} sales)</div>
                </div>
                <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
                  <div className="font-display font-bold mb-3 flex items-center gap-2"><Play className="w-5 h-5 text-[hsl(var(--primary))]" /> Top games by plays</div>
                  {analytics.top_games.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No published games yet.</p> :
                    analytics.top_games.map((g, i) => (
                      <div key={g.id} className="flex items-center gap-2 text-sm py-1.5" data-testid={`top-game-${i}`}>
                        <span className="w-5 text-center text-[hsl(var(--muted-foreground))]">{i + 1}</span>
                        <span className="text-lg">{g.cover_emoji}</span>
                        <span className="flex-1 truncate font-medium">{g.title}</span>
                        <span className="font-bold flex items-center gap-1"><Play className="w-3 h-3" /> {g.plays || 0}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 flex flex-wrap gap-8">
                <div><div className="text-xs text-[hsl(var(--muted-foreground))] font-semibold">Arena events</div><div className="font-display text-3xl font-extrabold">{analytics.arena.events}</div></div>
                <div><div className="text-xs text-[hsl(var(--muted-foreground))] font-semibold">Arena entries</div><div className="font-display text-3xl font-extrabold">{analytics.arena.submissions}</div></div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="asset-hub" className="mt-4 space-y-4" data-testid="admin-asset-hub">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <div className="font-display font-bold mb-1">Asset API Hub providers</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">Enable/disable providers and set the AI-credit cost per generation. Keys are configured server-side.</p>
            <div className="space-y-2">
              {hubProviders.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--border))]" data-testid={`admin-provider-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.name} <span className="text-[hsl(var(--muted-foreground))] font-normal">· {p.category}</span></div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{p.provider} {!p.configured && "· ⚠️ no API key"} {p.status === "coming_soon" && "· coming soon"}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-[hsl(var(--muted-foreground))]">cr</span>
                    <input type="number" defaultValue={p.credit_cost} min={0} onBlur={(e) => patchProvider(p.id, { credit_cost: Number(e.target.value) })}
                      className="w-16 rounded-lg border border-[hsl(var(--border))] bg-transparent px-2 py-1" data-testid={`provider-cost-${p.id}`} />
                  </div>
                  <Button size="sm" variant={p.enabled ? "default" : "outline"} className="rounded-full"
                    disabled={p.status === "coming_soon"}
                    onClick={() => patchProvider(p.id, { enabled: !p.enabled })} data-testid={`provider-toggle-${p.id}`}>
                    {p.enabled ? "Enabled" : "Disabled"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
          {hubUsage && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
              <div className="font-display font-bold mb-2">Hub usage</div>
              <div className="text-sm mb-2">Total generations: <b>{hubUsage.total_generations}</b></div>
              {hubUsage.by_provider.map((r) => (
                <div key={r.provider_id} className="flex justify-between text-sm py-1" data-testid={`hub-usage-${r.provider_id}`}><span>{r.provider_id}</span><span className="font-semibold">{r.count} gen · {r.credits} cr</span></div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="games" className="mt-4">
          {/* ANCHOR_GAMES */}
          <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
            {games.map((g, i) => (
              <div key={g.id} className={`flex items-center gap-3 px-4 py-3 ${i % 2 ? "bg-[hsl(var(--secondary))]/30" : ""}`}>
                <span className="text-2xl">{g.cover_emoji}</span>
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{g.title}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{g.owner_name} · {g.status}</div></div>
                <span className="text-xs flex items-center gap-1"><Play className="w-3 h-3" /> {g.plays}</span>
                {g.status === "published" && (
                  <Button size="sm" variant={g.featured ? "default" : "outline"} className="rounded-full" onClick={() => feature(g.id, "featured", !g.featured)} data-testid={`feature-${g.id}`}>
                    <Star className="w-3.5 h-3.5" /> {g.featured ? "Featured" : "Feature"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
            {users.map((u, i) => (
              <div key={u.id} className={`flex items-center gap-3 px-4 py-3 ${i % 2 ? "bg-[hsl(var(--secondary))]/30" : ""}`}>
                <span className="text-xl">{u.avatar_emoji}</span>
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{u.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{u.email}</div></div>
                <Badge variant="secondary">{u.role}</Badge>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          {usage && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
                <div className="font-display font-bold mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> AI usage by type</div>
                {Object.entries(usage.by_kind).length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No AI usage yet.</p> :
                  Object.entries(usage.by_kind).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1.5"><span className="capitalize">{k}</span><span className="font-bold">{v} credits</span></div>
                  ))}
              </div>
              <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
                <div className="font-display font-bold mb-2">Total AI calls</div>
                <div className="font-display text-4xl font-extrabold">{usage.total_calls}</div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ModRow = ({ icon, title, sub, actions }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 flex items-center gap-4">
    <span className="text-2xl">{icon}</span>
    <div className="flex-1 min-w-0"><div className="font-semibold truncate">{title}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{sub}</div></div>
    <div className="flex gap-2">{actions}</div>
  </div>
);
